import { useState } from "react";
import { Link } from "react-router-dom";
import Subtitle from "../typography/Subtitle";
import Title from "../typography/Title";
import Description from "../typography/Description";
import AudioPlayer from "./AudioPlayer";
import videoUrl from "../../assets/featureVid.mp4";
import videoUrl2 from "../../assets/features2.mp4";
import imgUrl from "../../assets/summary.png";
import { ArrowUpRightIcon } from "@phosphor-icons/react";

const modules = [
  {
    id: 1,
    tab: "CV Matching",
    label: "Screening",
    title: "Instantly rank every applicant against your JD",
    description:
      "AI ranks CVs against your Job Description for quick shortlisting. Instantly identifies top candidates based on skills, experience, and job requirements — so your team only reviews the best fits.",
    highlights: [
      "Stack rank candidates automatically",
      "Filter by relevant candidate profiles",
    ],
    link: "/product/jd-cv-matching",
    asset: (
      <video className="h-full w-full object-cover" autoPlay muted loop>
        <source src={videoUrl} />
      </video>
    ),
  },
  {
    id: 2,
    tab: "Sourcing",
    label: "Sourcing",
    title: "Source from 10+ job boards, automatically",
    description:
      "AI Recruiter sources candidates from 10+ job boards, cross-posts your role, promotes it, and screens candidates automatically based on your criteria.",
    highlights: [
      "Cross-post & promote your role automatically",
      "AI screens and delivers top candidates",
    ],
    link: "",
    asset: (
      <video className="h-full w-full object-cover" autoPlay muted loop>
        <source src={videoUrl2} />
      </video>
    ),
  },
  {
    id: 3,
    tab: "Phone Agent",
    label: "Phone Screening",
    title: "AI-powered phone interviews at scale",
    description:
      "AI Recruiter conducts phone interviews with candidates to assess their fit for the role, covering salary expectations, location preferences, and key qualifications — managing hundreds of calls in parallel.",
    highlights: [
      "AI-powered candidate outreach over phone",
      "Get assessment scores & communication skills",
    ],
    link: "/product/ai-phone-screener",
    asset: <AudioPlayer />,
  },
  {
    id: 4,
    tab: "Video Interview",
    label: "In-Depth Assessment",
    title: "Deep, probing AI video interviews",
    description:
      "AI conducts in-depth video interviews with candidates, assessing skillsets, work experience, culture fit, and providing detailed evaluation reports with full proctoring and candidate verification.",
    highlights: [
      "Audio + video recordings with transcripts",
      "Fully proctored with candidate verification",
    ],
    link: "/product/ai-interviews",
    asset: (
      <img
        className="h-full w-full object-cover"
        src={imgUrl}
        alt="In-Depth Skill Interviews—Automated & Authentic"
      />
    ),
  },
  {
    id: 5,
    tab: "Aptitude Tests",
    label: "Psychometric Testing",
    title: "Evaluate cognitive abilities and personality",
    description:
      "MCQ-based aptitude and psychometric assessments to evaluate cognitive abilities and personality traits. Fully customizable with 20-30 question tests tailored to your needs.",
    highlights: [
      "Conduct in-depth aptitude & psychometric tests",
      "Customize your own assessments",
    ],
    link: "",
    asset: (
      <img
        src={require("../../assets/modules/img5.png")}
        className="h-full w-full object-contain"
        alt="Aptitude & Personality Tests"
      />
    ),
  },
  {
    id: 6,
    tab: "Coding Tests",
    label: "Technical Assessment",
    title: "Comprehensive coding evaluations, fully proctored",
    description:
      "Comprehensive coding evaluations with support for 10+ programming languages. Deeply proctored with real-time monitoring and plagiarism detection.",
    highlights: [
      "10+ programming languages supported",
      "Real-time monitoring and plagiarism detection",
    ],
    link: "/product/coding-assessment",
    asset: (
      <img
        src={require("../../assets/modules/img6.png")}
        className="h-full w-full object-contain"
        alt="Coding Assessments"
      />
    ),
  },
];

const HowItWorks = () => {
  const [activeId, setActiveId] = useState(1);
  const active = modules.find((m) => m.id === activeId)!;

  return (
    <section className="relative flex w-screen snap-start snap-always flex-col items-center overflow-hidden bg-gray-950 px-4 py-12 md:py-16">
      <Subtitle>Products</Subtitle>
      <Title as="h2" className="mt-4 max-w-screen-md">
        How It Works: Humans + AI Agents
      </Title>
      <Description className="mt-4 max-w-screen-sm">
        A modular AI recruitment pipeline — pick the agents you need, and let
        them handle the heavy lifting end to end.
      </Description>

      {/* Horizontal Tabs */}
      <div className="mx-auto mt-10 flex w-full max-w-screen-lg items-center gap-1 overflow-x-auto border-b border-gray-700 md:mt-14">
        {modules.map((m) => (
          <button
            key={m.id}
            onClick={() => setActiveId(m.id)}
            className={`shrink-0 whitespace-nowrap px-5 py-3 font-serif text-sm font-medium tracking-wide transition-colors ${
              m.id === activeId
                ? "border-b-2 border-app-400 text-app-400"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            {m.tab}
          </button>
        ))}
      </div>

      {/* Two-Column Content */}
      <div className="mx-auto mt-8 flex w-full max-w-screen-lg flex-col gap-8 md:mt-12 md:flex-row md:gap-12">
        {/* Asset / Image */}
        <div className="flex h-64 w-full items-center justify-center overflow-hidden rounded-xl border border-white/[0.06] bg-gray-900 md:h-[22rem] md:w-1/2">
          {active.asset}
        </div>

        {/* Text Content */}
        <div className="flex w-full flex-col justify-center md:w-1/2">
          <span className="mb-3 inline-block w-fit rounded-full bg-app-400/10 px-3 py-1 font-serif text-xs font-medium uppercase tracking-[3px] text-app-400">
            {active.label}
          </span>
          <h3 className="font-serif text-2xl font-thin text-white md:text-3xl lg:text-4xl">
            {active.title}
          </h3>
          <p className="mt-4 font-sans text-sm leading-relaxed tracking-wide text-gray-400">
            {active.description}
          </p>
          <ul className="mt-6 flex flex-col gap-3">
            {active.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-app-400" />
                <span className="font-sans text-sm text-gray-300">{h}</span>
              </li>
            ))}
          </ul>
          {active.link && (
            <Link
              to={active.link}
              className="mt-6 inline-flex items-center gap-1 font-serif text-sm font-medium text-app-400 transition-colors hover:text-app-300"
            >
              Learn more
              <ArrowUpRightIcon size={16} />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
